import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { BatteryCharging, Car, Layers, Zap } from 'lucide-react'

type Status = 'live' | 'in-development' | 'poc'

interface Product {
  icon: React.ElementType
  name: string
  tagline: string
  description: string
  details: string[]
  assetType: string
  connection: string
  market: string
  poolPurpose: string
  status: Status
}

const products: Product[] = [
  {
    icon: Car,
    name: 'EV C2C Intraday',
    tagline: 'Cloud-connected EV charging flexibility on the intraday market',
    description:
      'Cloud-connected EVs are aggregated into a pool and their charging flexibility is traded on the intraday market (and Day-Ahead). gridX handles aggregation (nowcast + forecast), disaggregation, and pool configuration via API. EV driver charging preferences are always prioritised.',
    details: [
      'Charging commands are delivered via cloud API (c2c) — no local gateway required',
      'Disaggregation runs every 15 minutes using departure-priority scheduling',
      'An EV flex-band forecast is automatically provisioned for each pool',
      'Binary charge/stop commands; fallback behaviour controls charging when no task is scheduled',
    ],
    assetType: 'Cloud-connected EVs (e.g. Enode)',
    connection: 'Cloud API (c2c)',
    market: 'Intraday (+ Day-Ahead)',
    poolPurpose: 'intraday_cloud',
    status: 'live',
  },
  {
    icon: BatteryCharging,
    name: 'Home Battery Imbalance',
    tagline: 'gridBox-connected home batteries steered for passive imbalance optimisation',
    description:
      'gridBox-connected home batteries are pooled and steered for passive imbalance optimisation. The battery is fully dedicated to market trading — it is not combined with self-consumption optimisation (SSO), Time-of-Use, or other household optimisation. Compatible with Solax and Enphase inverters.',
    details: [
      'A zero-power default task is always active when no explicit task is scheduled',
      'PID controller tracks a continuous power setpoint with 10-second disaggregation',
      'Power allocated proportionally to each battery\'s available energy capacity',
      'Batteries must be exclusively dedicated to this pool — no shared household optimisation',
    ],
    assetType: 'Batteries via gridBox gateway',
    connection: 'gridBox (local gateway)',
    market: 'Imbalance',
    poolPurpose: 'imbalance',
    status: 'live',
  },
  {
    icon: Layers,
    name: 'Flex Value Stacking',
    tagline: 'Multi-asset optimisation combining household efficiency with pool-level trading',
    description:
      'Combines household-level optimisation (Energy Optimizer / Tariff Timer) with pool-level trading on the same system. The battery serves both self-consumption and market trading simultaneously.',
    details: [
      'Work in Progress — this feature is under active development. Details on supported capabilities and the roadmap will be added as the PoC matures.',
    ],
    assetType: 'Batteries via gridBox gateway',
    connection: 'gridBox (local gateway)',
    market: 'Intraday + ToU value stacking',
    poolPurpose: 'tou_stacking_poc',
    status: 'in-development',
  },
  {
    icon: Zap,
    name: 'Bidirectional Charging / V2G',
    tagline: 'Vehicle-to-grid PoC — discharge EV batteries back to the grid',
    description:
      'Local gateway-connected EVs are used for vehicle-to-grid (V2G) — discharging the EV battery back to the grid when market conditions are favourable. SSO runs as the baseline, with the EV controlled by the Flex layer on top.',
    details: [
      'Uses local gridBox gateway for EV control — no cloud API dependency',
      'Bidirectional charging is not yet regulatory-approved in all markets',
      'Regulatory changes (e.g. DE MiSpeL process) expected to expand availability from 2027',
      'Still in testing phase',
    ],
    assetType: 'Gateway-connected EVs (bidirectional)',
    connection: 'gridBox (local gateway)',
    market: 'Intraday (V2G)',
    poolPurpose: 'intraday',
    status: 'poc',
  },
]

const statusConfig: Record<Status, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  live: { label: 'Live', variant: 'default' },
  'in-development': { label: 'In Development', variant: 'secondary' },
  poc: { label: 'PoC', variant: 'outline' },
}

export default function DocsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Flex Products</h1>
        <p className="mt-1 text-muted-foreground">
          Overview of the four XENON Flex products, their asset types, markets, and current status.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {products.map((product) => {
          const Icon = product.icon
          const status = statusConfig[product.status]
          return (
            <Card key={product.name} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-muted p-2">
                      <Icon className="h-5 w-5 text-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{product.name}</CardTitle>
                      <CardDescription className="mt-0.5">{product.tagline}</CardDescription>
                    </div>
                  </div>
                  <Badge variant={status.variant} className="shrink-0">
                    {status.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <p className="text-sm text-muted-foreground">{product.description}</p>

                <ul className="space-y-1.5">
                  {product.details.map((detail) => (
                    <li key={detail} className="flex items-start gap-2 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
                      {detail}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-muted/50 p-3 text-xs">
                  <div>
                    <span className="font-medium text-foreground">Asset</span>
                    <p className="text-muted-foreground">{product.assetType}</p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Connection</span>
                    <p className="text-muted-foreground">{product.connection}</p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Market</span>
                    <p className="text-muted-foreground">{product.market}</p>
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Pool purpose</span>
                    <p className="font-mono text-muted-foreground">{product.poolPurpose}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
